/**
 * CON-1704 — Blocked NANP regions. Client mirror of
 * server/helpers/blockedRegions.js.
 *
 * MIRRORED, NOT IMPORTED: the server and client are separate repositories,
 * so this list is duplicated deliberately. If you change one, change the
 * other. Both repos pin the 23 area codes literally in their test suites
 * (server/tests/blockedRegions.test.js and
 * src/constants/__tests__/blockedRegions.test.js), so an edit inside either
 * repo fails a test — but drift BETWEEN the repos is only caught by whoever
 * reads this comment. The list is NANP country area codes, which change on
 * the order of once a decade; that is why the duplication was accepted over
 * adding a field to the contact API.
 *
 * These area codes look like US numbers and are not. A consultant's contact
 * list holds them because a digit was mistyped (806 -> 809), which is why
 * the badge copy leads with the country and says "probably mistyped".
 *
 * US TERRITORIES ARE DELIBERATELY ABSENT and must stay that way — they are
 * real US destinations CP serves. A false positive here silently tells a
 * real user in Puerto Rico or Guam that their number cannot be texted:
 *
 *     340 US Virgin Islands   787 / 939 Puerto Rico
 *     671 Guam                670 Northern Mariana Islands
 *     684 American Samoa
 *
 * Country names carry their article ("the Bahamas") for use in sentences;
 * getBlockedRegionLabel strips it for the chip label.
 */

export const BLOCKED_NANP_REGIONS = {
  809: 'the Dominican Republic',
  829: 'the Dominican Republic',
  849: 'the Dominican Republic',
  876: 'Jamaica',
  658: 'Jamaica',
  868: 'Trinidad & Tobago',
  242: 'the Bahamas',
  246: 'Barbados',
  441: 'Bermuda',
  284: 'the British Virgin Islands',
  345: 'the Cayman Islands',
  473: 'Grenada',
  664: 'Montserrat',
  869: 'St. Kitts & Nevis',
  758: 'St. Lucia',
  784: 'St. Vincent & the Grenadines',
  649: 'the Turks & Caicos Islands',
  264: 'Anguilla',
  268: 'Antigua & Barbuda',
  297: 'Aruba',
  599: 'Curacao',
  767: 'Dominica',
  721: 'Sint Maarten',
};

/**
 * getBlockedRegion
 *
 * EXACT 3-digit area-code lookup, matching the server helper of the same
 * name. No prefix matching, no inference from carrier name or country code.
 * Anything that is not a 10-digit NANP number (or 11 digits with a leading
 * 1) is not blocked.
 *
 * @param {string} phoneNumber - any format
 * @returns {{ blocked: boolean, areaCode: string|null, country: string|null }}
 */
export function getBlockedRegion(phoneNumber) {
  const notBlocked = { blocked: false, areaCode: null, country: null };

  if (!phoneNumber) {
    return notBlocked;
  }

  const digits = String(phoneNumber).replace(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return notBlocked;
  }

  const areaCode = normalized.slice(0, 3);
  const country = BLOCKED_NANP_REGIONS[areaCode];

  if (!country) {
    return notBlocked;
  }

  return { blocked: true, areaCode, country };
}

/**
 * getBlockedRegionLabel
 *
 * The chip label: the country without its leading article, because
 * "the Dominican Republic" reads wrong as a standalone chip.
 */
export function getBlockedRegionLabel(country) {
  return String(country || '').replace(/^the /, '');
}
